import { Check, CaretRight } from "phosphor-react";
import { Avatar } from "@shared/ui/Avatar";
import { Checkbox, CHECKBOX_SIZES } from "@shared/ui/Inputs";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import {
  MULTISELECT_VARIANTS,
  MultiSelectVariantType,
} from "./MultiSelect/AsyncMultiSelectDropdown";
import { useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

type DropdownItemPropsType = {
  option: any;
  found: boolean;
  showAvatar?: boolean;
  onClick: () => void;
  maxLabelWidth?: string;
  customOptionItem?: (option: any) => React.ReactNode;
  multiSelect?: boolean;
  renderLabel?: (option: any) => React.ReactNode;
  variant?: MultiSelectVariantType;
  isHovered?: boolean;
  onChildSelect?: (child: any) => void;
  selectedItems?: any[];
  onResetHover?: () => void;
};

const DropdownItem = ({
  option,
  found,
  showAvatar = false,
  onClick = () => {},
  maxLabelWidth = "",
  customOptionItem = null,
  multiSelect = false,
  renderLabel,
  variant = MULTISELECT_VARIANTS.DEFAULT,
  isHovered = false,
  onChildSelect = () => {},
  selectedItems = [],
  onResetHover = () => {},
}: DropdownItemPropsType) => {
  const Icon = option.iconName;
  const itemRef = useRef<HTMLDivElement>(null);
  const [childDropdownPos, setChildDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [hoveredChildValue, setHoveredChildValue] = useState<string | null>(
    null,
  );

  useLayoutEffect(() => {
    if (isHovered && itemRef.current && option.children?.length > 0) {
      const rect = itemRef.current.getBoundingClientRect();
      setChildDropdownPos({
        top: rect.top,
        left: rect.right,
      });
    } else {
      setChildDropdownPos(null);
    }
  }, [isHovered, option.children?.length]);

  useLayoutEffect(() => {
    if (!isHovered) {
      setHoveredChildValue(null);
    }
  }, [isHovered]);

  const ChildDropdown = () => (
    <div
      className="fixed w-48 bg-primary rounded-md shadow-lg border border-solid border-secondary overflow-hidden max-h-96 flex flex-col z-[9999]"
      style={{
        top: childDropdownPos?.top,
        left: `calc(${childDropdownPos?.left}px + 0.5rem)`,
      }}
    >
      <div className="px-md py-sm border-b border-solid border-secondary sticky top-0 bg-primary">
        <BodyText
          size={BODY_TEXT_SIZES.X_SMALL}
          color="text-secondary"
          className="font-semibold"
        >
          {option.label}
        </BodyText>
      </div>
      <div className="overflow-y-auto flex-1">
        {option.children.map((child: any) => (
          <div
            key={child.value}
            onMouseEnter={() => setHoveredChildValue(child.value)}
            onMouseLeave={() => setHoveredChildValue(null)}
          >
            <DropdownItem
              option={child}
              found={
                selectedItems?.some(
                  (item: any) => item.value === child.value,
                ) || false
              }
              showAvatar={false}
              onClick={() => {
                onChildSelect(child);
                setHoveredChildValue(null);
                onResetHover();
              }}
              multiSelect={multiSelect}
              renderLabel={renderLabel}
              variant={variant}
              isHovered={hoveredChildValue === child.value}
              onChildSelect={(grandchild) => {
                onChildSelect(grandchild);
                setHoveredChildValue(null);
                onResetHover();
              }}
              onResetHover={() => {
                setHoveredChildValue(null);
                onResetHover();
              }}
              selectedItems={selectedItems}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div key={option.value} className="relative w-full" ref={itemRef}>
      <div
        onClick={() => !option.disabled && onClick()}
        className={`w-full flex items-center justify-between py-sm px-md font-manrope text-xs font-medium text-primary transition-all duration-200 ease-in-out ${
          option.disabled ? "cursor-not-allowed" : "hover:bg-accent"
        } ${isHovered ? "bg-accent" : ""}`}
      >
        <div className="flex items-center min-w-0">
          {customOptionItem ? (
            customOptionItem(option)
          ) : (
            <>
              {showAvatar && (
                <Avatar
                  className="mr-sm shrink-0"
                  name={option.name || option.label}
                />
              )}

              {option.thumbnail && (
                <img
                  src={option.thumbnail}
                  className="w-3xl h-2xl rounded object-cover mr-sm shrink-0"
                  alt={option.thumbnail}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = "/images/default/thumbnail.svg";
                  }}
                />
              )}

              {option.icon && option.icon}

              {option.iconName && (
                <div className="flex items-center justify-center mr-sm shrink-0">
                  <Icon
                    width={option.size || 14}
                    height={option.size || 14}
                    className={option.className || ""}
                    style={option.iconStyle || {}}
                  />
                </div>
              )}

              <div className="flex flex-col min-w-0">
                <div className="flex items-center min-w-0">
                  {renderLabel ? (
                    renderLabel(option)
                  ) : (
                    <BodyText
                      size={BODY_TEXT_SIZES.X_SMALL}
                      color="text-primary"
                      className={`truncate ${
                        maxLabelWidth ||
                        (option.description ? "max-w-52" : "max-w-80")
                      }`}
                    >
                      {option.label}
                    </BodyText>
                  )}
                  {option.description && (
                    <BodyText
                      size={BODY_TEXT_SIZES.X_SMALL}
                      color="text-secondary"
                      className="max-w-36 truncate shrink-0"
                    >
                      <span className="text-primary">&nbsp;•&nbsp;</span>
                      {option.description}
                    </BodyText>
                  )}
                </div>

                {option.access && (
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-tertiary"
                    className="mt-2xs truncate !text-[10px]"
                  >
                    {option.access}
                  </BodyText>
                )}
              </div>
            </>
          )}
        </div>
        {found && !multiSelect && (
          <div className="flex items-center justify-center ml-sm shrink-0">
            <Check width={16} height={16} className="text-brand" />
          </div>
        )}
        {multiSelect &&
        variant === MULTISELECT_VARIANTS.DUAL_PANE &&
        option.children ? (
          <CaretRight width={16} height={16} className="text-secondary" />
        ) : multiSelect ? (
          <div className="shrink-0 ml-sm">
            <Checkbox size={CHECKBOX_SIZES.SMALL} checked={found} />
          </div>
        ) : null}
      </div>
      {childDropdownPos &&
        isHovered &&
        option.children &&
        option.children.length > 0 &&
        createPortal(<ChildDropdown />, document.body)}
    </div>
  );
};

export default DropdownItem;
